import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { whatsappLink } from "@/lib/dtf";
import { scrollToSection, scrollToTop } from "@/lib/scroll";
import { LOGO_URL } from "@/config/assets";

const links = [
  { label: "Calculadora DTF Têxtil", href: "#calculadora", testId: "nav-link-calculator" },
  { label: "Como funciona", href: "#como-funciona", testId: "nav-link-how-it-works" },
  {
    label: "Como enviar minha arte DTF",
    href: "#regras-arte",
    testId: "nav-link-art-rules",
  },
  { label: "Dúvidas", href: "#duvidas", testId: "nav-link-faq" },
];

export const Logo = ({ dark = false, className = "" }) => (
  <img
    src={LOGO_URL}
    alt="ClickDTF"
    className={`w-auto select-none ${dark ? "brightness-0 grayscale invert" : ""} ${className || "h-14 sm:h-16"}`}
  />
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [solid, setSolid] = useState(false);

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  const go = (href) => {
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid ? "border-b border-neutral-200/80 bg-white/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-24 max-w-7xl items-center justify-between px-5 lg:px-8">
        <a
          href="#top"
          data-testid="header-logo"
          onClick={(e) => {
            e.preventDefault();
            scrollToTop();
          }}
          className="flex items-center gap-2"
          aria-label="ClickDTF — início"
        >
          <Logo />
        </a>

        <div className="hidden items-center gap-7 md:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testId}
              onClick={(e) => {
                e.preventDefault();
                go(l.href);
              }}
              className="link-line text-sm font-medium text-neutral-700 transition-colors hover:text-black"
            >
              {l.label}
            </a>
          ))}
          <a
            href={whatsappLink("Olá! Vim pelo ClickDTF e gostaria de pedir minha impressão DTF têxtil.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-whatsapp"
            className="inline-flex items-center gap-2 rounded-full bg-[#f58244] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_24px_-10px_rgba(245,130,68,0.9)] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#e06f32]"
          >
            <MessageCircle className="h-4 w-4" strokeWidth={2.4} />
            Envie sua arte agora
          </a>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-trigger"
          aria-label="Abrir menu"
          aria-expanded={open}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-neutral-200 bg-white md:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="border-t border-neutral-200 bg-white px-5 pb-7 pt-4 md:hidden"
          >
            <ul className="flex flex-col">
              {links.map((l, i) => (
                <li key={l.href} className="border-b border-neutral-100">
                  <a
                    href={l.href}
                    data-testid={`mobile-${l.testId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      go(l.href);
                    }}
                    className="flex items-center gap-3 py-4 text-base font-medium text-black"
                  >
                    <span className="num text-xs text-[#f58244]">
                      0{i + 1}
                    </span>
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
            <a
              href={whatsappLink("Olá! Vim pelo ClickDTF e gostaria de pedir minha impressão DTF têxtil.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-link-order"
              className="mt-5 flex items-center justify-center gap-2 rounded-full bg-[#f58244] px-6 py-3.5 text-base font-semibold text-white"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={2.4} />
              Envie sua arte agora
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
